import { useEffect, useState } from 'react';
import { Trash2, FileDown, Eye, Undo2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { usePendingClients, usePendingEngagements } from '@/api/hooks';
import { api, ApiError } from '@/api/client';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useToast } from '@/components/Toast';
import { fmtDateTime, clientSubId } from '@/lib/format';

const STATUS = ['', 'Yes', 'No', 'N/A'];
// Mandatory statuses (Client Manager / Return Manager): no blank option, default 'No'.
const REQ_STATUS = ['Yes', 'No', 'N/A'];
// A request is "being completed" when its statuses are all Yes/N/A (Power Apps rule).
// The stored proc then writes CompletionDate, so a date is required for those rows.
const DONE = ['Yes', 'N/A'];
const clientDone = (m: string, c: string) => DONE.includes(m) && DONE.includes(c);
const engDone = (s: string[]) => s.every((x) => DONE.includes(x));
const DATE_MSG = 'Please enter the completion date to submit the form.';

type Row = Record<string, unknown>;

// Submit = complete the whole grid; delC/delE = delete one request (Power Apps
// confirms deletes before calling the proc).
type Confirm =
  | { kind: 'submitC' }
  | { kind: 'submitE' }
  | { kind: 'delC'; row: Row }
  | { kind: 'delE'; row: Row };

export function Pending() {
  const notify = useToast();
  const qc = useQueryClient();
  const clients = usePendingClients();
  const engagements = usePendingEngagements();

  const [clientEdits, setClientEdits] = useState<Record<number, Row>>({});
  const [engEdits, setEngEdits] = useState<Record<number, Row>>({});
  const [confirm, setConfirm] = useState<Confirm | null>(null);

  useEffect(() => { setClientEdits({}); }, [clients.data]);
  useEffect(() => { setEngEdits({}); }, [engagements.data]);

  const cRows = clients.data ?? [];
  const eRows = engagements.data ?? [];

  function cVal(i: number, key: string, fallback: unknown) {
    return (clientEdits[i]?.[key] ?? fallback ?? '') as string;
  }
  function eVal(i: number, key: string, fallback: unknown) {
    return (engEdits[i]?.[key] ?? fallback ?? '') as string;
  }

  // Row is being completed (all statuses Yes/N/A) but has no completion date yet.
  const clientNeedsDate = (i: number, r: Row) =>
    clientDone(cVal(i, 'MaconomyStatus', r.MaconomyStatus), cVal(i, 'ConstantStatus', r.ConstantStatus)) &&
    !cVal(i, 'CompletionDate', r.CompletionDate);
  const engNeedsDate = (i: number, r: Row) =>
    engDone([
      eVal(i, 'MaconomyStatus', r.MaconomyStatus),
      eVal(i, 'AxcessClientProfileStatus', r.AxcessClientProfileStatus),
      eVal(i, 'ReturnManagerStatus', r.ReturnManagerStatus),
      eVal(i, 'AxcessWorkflowStatus', r.AxcessWorkflowStatus),
      eVal(i, 'CCHEngagementStatus', r.CCHEngagementStatus),
      eVal(i, 'EngagementLetterStatus', r.EngagementLetterStatus),
    ]) && !eVal(i, 'CompletionDate', r.CompletionDate);

  const rowLabel = (r: Row) => String(r.ClientName || `${r.ClientID ?? ''}${r.ClientSubID ? '/' + r.ClientSubID : ''}`);
  const clientBlocked = cRows.some((r, i) => clientNeedsDate(i, r));
  const engBlocked = eRows.some((r, i) => engNeedsDate(i, r));
  // Unsaved inline edits — enables "Discard Changes" (Power Apps re-reads the table).
  const clientDirty = Object.keys(clientEdits).length > 0;
  const engDirty = Object.keys(engEdits).length > 0;

  async function submitClients() {
    setConfirm(null);
    const missing = cRows.filter((r, i) => clientNeedsDate(i, r)).map(rowLabel);
    if (missing.length) { notify('error', `${DATE_MSG} Missing for: ${missing.join(', ')}.`); return; }
    const rows = cRows.map((r, i) => ({
      clientId: String(r.ClientID ?? ''),
      clientSubId: String(r.ClientSubID ?? ''),
      maconomyStatus: cVal(i, 'MaconomyStatus', r.MaconomyStatus),
      constantStatus: cVal(i, 'ConstantStatus', r.ConstantStatus),
      clientManagerStatus: cVal(i, 'ClientManagerStatus', r.ClientManagerStatus) || 'No',
      returnManagerStatus: cVal(i, 'ReturnManagerStatus', r.ReturnManagerStatus) || 'No',
      dateCompleted: cVal(i, 'CompletionDate', r.CompletionDate),
    }));
    await submit('/pending/clients/submit', rows, 'Client requests submitted.');
  }

  async function submitEngagements() {
    setConfirm(null);
    const missing = eRows.filter((r, i) => engNeedsDate(i, r)).map(rowLabel);
    if (missing.length) { notify('error', `${DATE_MSG} Missing for: ${missing.join(', ')}.`); return; }
    const rows = eRows.map((r, i) => ({
      engagementId: String(r.EngagementID ?? ''),
      maconomyStatus: eVal(i, 'MaconomyStatus', r.MaconomyStatus),
      axcessClientProfileStatus: eVal(i, 'AxcessClientProfileStatus', r.AxcessClientProfileStatus),
      returnManagerStatus: eVal(i, 'ReturnManagerStatus', r.ReturnManagerStatus),
      axcessWorkflowStatus: eVal(i, 'AxcessWorkflowStatus', r.AxcessWorkflowStatus),
      cchEngagementStatus: eVal(i, 'CCHEngagementStatus', r.CCHEngagementStatus),
      engagementLetterStatus: eVal(i, 'EngagementLetterStatus', r.EngagementLetterStatus),
      dateCompleted: eVal(i, 'CompletionDate', r.CompletionDate),
    }));
    await submit('/pending/engagements/submit', rows, 'Engagement requests submitted.');
  }

  // Submit completions; if the import step reports a warning, show it (the
  // completion still succeeded and the data moved to Completed Requests).
  async function submit(url: string, rows: unknown[], ok: string) {
    try {
      const res = await api.post<{ importWarning?: string }>(url, { rows });
      qc.invalidateQueries({ queryKey: ['pending'] });
      qc.invalidateQueries({ queryKey: ['counts'] });
      notify(res?.importWarning ? 'info' : 'success', res?.importWarning ?? ok);
    } catch (e) {
      notify('error', e instanceof ApiError ? e.problem.title : 'Action failed');
    }
  }

  async function run(fn: () => Promise<unknown>, ok: string) {
    try {
      await fn();
      notify('success', ok);
      qc.invalidateQueries({ queryKey: ['pending'] });
      qc.invalidateQueries({ queryKey: ['counts'] });
    } catch (e) {
      notify('error', e instanceof ApiError ? e.problem.title : 'Action failed');
    }
  }

  async function deleteClient(r: Row) {
    await run(
      () => api.del(`/pending/clients/${r.ClientID}/${r.ClientSubID}?clientType=${encodeURIComponent(String(r.ClientType ?? 'Master'))}`),
      'Client request deleted.',
    );
  }
  async function deleteEngagement(r: Row) {
    await run(() => api.del(`/pending/engagements/${r.EngagementID}`), 'Engagement request deleted.');
  }

  const StatusSel = ({ value, onChange, options = STATUS }: { value: string; onChange: (v: string) => void; options?: string[] }) => (
    <select className="input w-24" value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((s) => <option key={s} value={s}>{s || '—'}</option>)}
    </select>
  );

  let confirmMsg = 'Do you wish to submit these requests? Approved requests will move to Completed Requests.';
  if (confirm && (confirm.kind === 'delC' || confirm.kind === 'delE')) {
    const what = confirm.kind === 'delC' ? 'client' : 'engagement';
    confirmMsg = `Delete the ${what} request for ${rowLabel(confirm.row)}? This cannot be undone.`;
  }

  return (
    <div className="space-y-8 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-primary">Pending Requests</h1>
        <Link to="/pending/download" className="btn-secondary inline-flex items-center gap-1">
          <FileDown className="h-4 w-4" /> Download Pending Requests
        </Link>
      </div>

      {/* Client Requests */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-base font-semibold text-primary">Client Requests</h2>
          <div className="flex gap-2">
            <button className="btn-secondary inline-flex items-center gap-1" disabled={!clientDirty}
              title="Discard unsaved status / date edits"
              onClick={() => { setClientEdits({}); clients.refetch(); }}>
              <Undo2 className="h-4 w-4" /> Discard Changes
            </button>
            <button className="btn-primary" disabled={clientBlocked} title={clientBlocked ? DATE_MSG : ''}
              onClick={() => setConfirm({ kind: 'submitC' })}>Submit</button>
          </div>
        </div>
        <div className="max-h-[65vh] overflow-auto rounded-lg border border-border bg-card shadow-sm">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="sticky top-0 z-10 bg-secondary/60 text-xs uppercase text-muted-foreground">
              <tr>{['Request Type','Client/Sub ID','Client Name','Submitted','Maconomy','Constant','Client Manager','Return Manager','Date Completed','Submitted By',''].map((h) => <th key={h} className="table-cell text-left">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-border">
              {cRows.map((r, i) => (
                <tr key={i}>
                  <td className="table-cell">{String(r.RecordType ?? '')}</td>
                  <td className="table-cell">{clientSubId(r)}</td>
                  <td className="table-cell">{String(r.ClientName ?? '')}</td>
                  <td className="table-cell">{fmtDateTime(r.CreatedDate)}</td>
                  <td className="table-cell"><StatusSel value={cVal(i,'MaconomyStatus',r.MaconomyStatus)} onChange={(v)=>setClientEdits((s)=>({...s,[i]:{...s[i],MaconomyStatus:v}}))} /></td>
                  <td className="table-cell"><StatusSel value={cVal(i,'ConstantStatus',r.ConstantStatus)} onChange={(v)=>setClientEdits((s)=>({...s,[i]:{...s[i],ConstantStatus:v}}))} /></td>
                  <td className="table-cell"><StatusSel options={REQ_STATUS} value={cVal(i,'ClientManagerStatus',r.ClientManagerStatus)||'No'} onChange={(v)=>setClientEdits((s)=>({...s,[i]:{...s[i],ClientManagerStatus:v}}))} /></td>
                  <td className="table-cell"><StatusSel options={REQ_STATUS} value={cVal(i,'ReturnManagerStatus',r.ReturnManagerStatus)||'No'} onChange={(v)=>setClientEdits((s)=>({...s,[i]:{...s[i],ReturnManagerStatus:v}}))} /></td>
                  <td className="table-cell"><div className="flex items-center gap-1">
                    <input type="date" className="input w-36" value={cVal(i,'CompletionDate',r.CompletionDate)} onChange={(e)=>setClientEdits((s)=>({...s,[i]:{...s[i],CompletionDate:e.target.value}}))} />
                    {clientNeedsDate(i,r) && <span title={DATE_MSG} className="font-bold text-orange-500">!</span>}
                  </div></td>
                  <td className="table-cell">{String(r.CreatedBy ?? '')}</td>
                  <td className="table-cell">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/clients/${encodeURIComponent(String(r.ClientID ?? ''))}/${encodeURIComponent(String(r.ClientSubID ?? '0'))}`}
                        title="View Details" className="text-muted-foreground hover:text-primary">
                        <Eye className="h-4 w-4" />
                      </Link>
                      <button className="text-destructive hover:opacity-70" title="Delete Client Request"
                        onClick={()=>setConfirm({ kind: 'delC', row: r })}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {clients.isFetching && <tr><td className="table-cell" colSpan={11}>Loading…</td></tr>}
              {!clients.isFetching && cRows.length === 0 && <tr><td className="table-cell text-muted-foreground" colSpan={11}>No pending client requests.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {/* Engagement Requests */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-base font-semibold text-primary">Engagement Requests</h2>
          <div className="flex gap-2">
            <button className="btn-secondary inline-flex items-center gap-1" disabled={!engDirty}
              title="Discard unsaved status / date edits"
              onClick={() => { setEngEdits({}); engagements.refetch(); }}>
              <Undo2 className="h-4 w-4" /> Discard Changes
            </button>
            <button className="btn-primary" disabled={engBlocked} title={engBlocked ? DATE_MSG : ''}
              onClick={() => setConfirm({ kind: 'submitE' })}>Submit</button>
          </div>
        </div>
        <div className="max-h-[65vh] overflow-auto rounded-lg border border-border bg-card shadow-sm">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="sticky top-0 z-10 bg-secondary/60 text-xs uppercase text-muted-foreground">
              <tr>{['Type','Client/Sub ID','Client Name','Submitted','Maconomy','Client Profile','Return Mgr','Workflow','CCH Eng','EL','Date Completed','By',''].map((h) => <th key={h} className="table-cell text-left">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-border">
              {eRows.map((r, i) => (
                <tr key={i}>
                  <td className="table-cell">{String(r.RecordType ?? '')}</td>
                  <td className="table-cell">{String(r.ClientID ?? '')}</td>
                  <td className="table-cell">{String(r.ClientName ?? '')}</td>
                  <td className="table-cell">{fmtDateTime(r.CreatedDate)}</td>
                  <td className="table-cell"><StatusSel value={eVal(i,'MaconomyStatus',r.MaconomyStatus)} onChange={(v)=>setEngEdits((s)=>({...s,[i]:{...s[i],MaconomyStatus:v}}))} /></td>
                  <td className="table-cell"><StatusSel value={eVal(i,'AxcessClientProfileStatus',r.AxcessClientProfileStatus)} onChange={(v)=>setEngEdits((s)=>({...s,[i]:{...s[i],AxcessClientProfileStatus:v}}))} /></td>
                  <td className="table-cell"><StatusSel value={eVal(i,'ReturnManagerStatus',r.ReturnManagerStatus)} onChange={(v)=>setEngEdits((s)=>({...s,[i]:{...s[i],ReturnManagerStatus:v}}))} /></td>
                  <td className="table-cell"><StatusSel value={eVal(i,'AxcessWorkflowStatus',r.AxcessWorkflowStatus)} onChange={(v)=>setEngEdits((s)=>({...s,[i]:{...s[i],AxcessWorkflowStatus:v}}))} /></td>
                  <td className="table-cell"><StatusSel value={eVal(i,'CCHEngagementStatus',r.CCHEngagementStatus)} onChange={(v)=>setEngEdits((s)=>({...s,[i]:{...s[i],CCHEngagementStatus:v}}))} /></td>
                  <td className="table-cell"><StatusSel value={eVal(i,'EngagementLetterStatus',r.EngagementLetterStatus)} onChange={(v)=>setEngEdits((s)=>({...s,[i]:{...s[i],EngagementLetterStatus:v}}))} /></td>
                  <td className="table-cell"><div className="flex items-center gap-1">
                    <input type="date" className="input w-36" value={eVal(i,'CompletionDate',r.CompletionDate)} onChange={(e)=>setEngEdits((s)=>({...s,[i]:{...s[i],CompletionDate:e.target.value}}))} />
                    {engNeedsDate(i,r) && <span title={DATE_MSG} className="font-bold text-orange-500">!</span>}
                  </div></td>
                  <td className="table-cell">{String(r.CreatedBy ?? '')}</td>
                  <td className="table-cell">
                    <div className="flex items-center justify-end gap-2">
                      <Link to={`/engagements/${encodeURIComponent(String(r.EngagementID ?? ''))}`}
                        title="View Details" className="text-muted-foreground hover:text-primary">
                        <Eye className="h-4 w-4" />
                      </Link>
                      <button className="text-destructive hover:opacity-70" title="Delete Engagement Request"
                        onClick={()=>setConfirm({ kind: 'delE', row: r })}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {engagements.isFetching && <tr><td className="table-cell" colSpan={13}>Loading…</td></tr>}
              {!engagements.isFetching && eRows.length === 0 && <tr><td className="table-cell text-muted-foreground" colSpan={13}>No pending engagement requests.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <ConfirmDialog
        open={confirm !== null}
        message={confirmMsg}
        onConfirm={() => {
          const c = confirm;
          if (!c) return;
          if (c.kind === 'submitC') { submitClients(); return; }
          if (c.kind === 'submitE') { submitEngagements(); return; }
          setConfirm(null);
          if (c.kind === 'delC') deleteClient(c.row);
          else deleteEngagement(c.row);
        }}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}
